/// <amd-module name="Favorites/Panel/FavoritesListProvider" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { Button } from "VSSUI/Button";
import { css } from "VSSUI/Util";

import { ArtifactScope, Favorite, FavoriteProvider } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";

import { FavoriteStorageScopes } from "Favorites/Common/Constants";
import { IFavoriteProviderDataService } from "Favorites/Common/Contracts/Service";
import * as FavoriteResources from "Favorites/Common/Resources";
import { FavoritesUserSettings, IFavoriteSettingsCollection, IFavoritesUserSettingsService } from "Favorites/Panel/FavoritesUserSettingsService";
import * as FavoritePanelResources from "Favorites/Panel/Resources";

import { CrossServiceFavoritesProvider } from "CrossServiceFavoritesProvider";
import * as FavoritesTelemetry from "Favorites/Common/Telemetry";
import { IObservable, IObservableArrayEventArgs, ObservableArray, ObservableArrayAction, ObservableValue } from "VSS/Core/Observable";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IItemIndicatorProps, ItemIndicatorSet } from "VSSUI/ItemIndicator";
import { renderListCell } from "VSSUI/List";
import { IListBoxGroup, IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, SimpleTableCell } from "VSSUI/Table";
import { IItemProvider } from "VSSUI/Utilities/Provider";

export interface IFavoriteListOptions {
    /**
     * Context of the page using the provider.
     */
    pageContext: IVssPageContext;
    /**
     * Scope to in which favorites reside, used to filter the set of favorites the caller
     * is interested in.
     */
    artifactScope: ArtifactScope;
    /**
     * (Optional)The set of favorite artifact types that the provider should not return to the component
     * rendering to the UI.
     */
    artifactTypesToHide?: string[];
    /**
     * Allows consumers to react to the completion of the load sequence for Favorites.
     */
    onLoad?: () => void;
}

export interface FavoriteItem {
    favorite: Favorite;
    favoriteProvider: FavoriteProvider;
    clientDataService?: IFavoriteProviderDataService;
}

export class FavoritesListProvider
    implements IItemProvider<IListBoxItem<FavoriteItem>>, IObservable<IObservableArrayEventArgs<IListBoxItem<FavoriteItem>>, ObservableArrayAction>
{
    private pageContext: IVssPageContext;
    private artifactScope: ArtifactScope;
    private artifactTypesToHide?: Set<string>;

    private favoritesService: IFavoritesService;
    private crossServiceFavoritesProvider: CrossServiceFavoritesProvider;
    private favoritesUserSettingsService: IFavoritesUserSettingsService;

    private items: ObservableArray<IListBoxItem<FavoriteItem>>;
    private itemsPromise: Promise<ObservableArray<IListBoxItem<FavoriteItem>> | FavoriteItem[]>;
    private itemGroupMapping: { [artifactType: string]: IListBoxGroup };
    private resolvedOrderedGroups?: IListBoxGroup[];
    private favorites: Favorite[];
    private favoritesRemoved: { [artifactId: string]: FavoriteItem };
    private allFavoritesAreLoaded: boolean;
    public isLoading: boolean;
    private providers: { [artifactType: string]: FavoriteProvider };
    private services: (IFavoriteProviderDataService | undefined)[];

    public onLoadCompleted?: () => void;

    constructor(options: IFavoriteListOptions) {
        const { artifactScope, pageContext, artifactTypesToHide, onLoad } = options;

        this.pageContext = pageContext;
        this.artifactScope = artifactScope;
        this.artifactTypesToHide = new Set<string>(artifactTypesToHide);
        this.favorites = [];
        this.items = new ObservableArray();
        this.isLoading = true;
        this.allFavoritesAreLoaded = false;
        this.providers = {};
        this.services = [];

        this.favoritesService = this.pageContext.getService<IFavoritesService>("IFavoritesService");
        this.favoritesUserSettingsService = this.pageContext.getService<IFavoritesUserSettingsService>("IFavoritesUserSettingsService");
        this.crossServiceFavoritesProvider = new CrossServiceFavoritesProvider(pageContext);

        this.onLoadCompleted = onLoad;
    }

    public get length(): number {
        return this.items.length;
    }

    public get value(): IListBoxItem<FavoriteItem>[] {
        return this.items.value;
    }

    public getGroups(): IListBoxGroup[] {
        if (!this.resolvedOrderedGroups && this.items) {
            const groups: IListBoxGroup[] = [];
            const groupFound = new Set<string>();
            this.itemGroupMapping &&
                Object.keys(this.itemGroupMapping).forEach((artifactType) => {
                    if (!this.artifactTypesToHide || (this.artifactTypesToHide && !this.artifactTypesToHide.has(artifactType))) {
                        const group = this.itemGroupMapping[artifactType];
                        if (groups) {
                            const id = group.id.toString();
                            if (!groupFound.has(id)) {
                                groups.push(group);
                                groupFound.add(id);
                            }
                        }
                    }
                });

            groups.sort((a, b) => {
                // doing a numeric comparison as the key is always a no (matching the order of the group)
                return +a.id - +b.id;
            });

            this.resolvedOrderedGroups = groups;
        }

        return this.resolvedOrderedGroups!;
    }

    public isLoaded(): boolean {
        return this.allFavoritesAreLoaded;
    }

    public getListItem = (item: FavoriteItem): IListBoxItem<FavoriteItem> => {
        const group = this.getGroups().filter((g) => g.name === item.favoriteProvider.pluralName)[0];
        const order = group ? group.id : item.favoriteProvider.order;
        const itemUrl = this.getArtifactLink(item.favorite);

        const renderItemContent = (
            rowIndex: number,
            columnIndex: number,
            tableColumn: ITableColumn<IListBoxItem>,
            tableItem: IListBoxItem
        ): JSX.Element => {
            const showFavoriteIcon = !!item.favorite.id;
            const isRemoved = this.isRemoved(item);

            let isFavoriteRemoved = new ObservableValue<boolean>(isRemoved);
            const onClick = (ev: React.MouseEvent<HTMLElement>) => {
                this.toggleFavorite(item);
                isFavoriteRemoved.value = !isFavoriteRemoved.value;
                ev.preventDefault();
            };

            return (
                <SimpleTableCell
                    className={css("favorite-item", tableColumn.className, tableItem.className)}
                    columnIndex={columnIndex}
                    key={columnIndex}
                    tableColumn={tableColumn}
                >
                    {tableItem &&
                        renderListCell(
                            {
                                ...tableItem,
                                textNode: undefined,
                                textClassName: "flex-grow artifact-link no-underline-link"
                            }
                        )}
                    {showFavoriteIcon && (
                        <Observer isRemoved={isFavoriteRemoved}>
                            {({ isRemoved }: { isRemoved: boolean }) => {
                                return (
                                    <span className="vss-ItemIndicator">
                                        <Button
                                            ariaLabel={
                                                isRemoved
                                                    ? FavoriteResources.FavoriteStarUnfavoritedTitle
                                                    : FavoriteResources.FavoriteStarFavoritedTitle
                                            }
                                            ariaPressed={!isRemoved}
                                            className="vss-FavoritePanel--IconButton"
                                            iconProps={{
                                                className: "vss-FavoriteItemPicker--starIcon ms-font-l",
                                                iconName: isRemoved ? "FavoriteStar" : "FavoriteStarFill"
                                            }}
                                            onClick={onClick}
                                            subtle={true}
                                            tooltipProps={{
                                                text: isRemoved
                                                    ? FavoriteResources.FavoriteStarUnfavoritedTitle
                                                    : FavoriteResources.FavoriteStarFavoritedTitle
                                            }}
                                            tabIndex={0}
                                        />
                                    </span>
                                );
                            }}
                        </Observer>
                    )}
                </SimpleTableCell>
            );
        };

        return {
            id: item.favorite.id,
            iconProps: {
                iconName: item.favoriteProvider.iconName,
                className: item.favoriteProvider.iconClass
            },
            groupId: order.toString(),
            href: itemUrl,
            text: this.getDisplayName(item),
            render: renderItemContent
        };
    };

    public onSelectedItemChanged = (item: FavoriteItem): void | boolean => {
        FavoritesTelemetry.publishNavigateEvent(this.pageContext, item.favorite.artifactType, "Favorites Panel");
        if (this.canArtifactNavigateFPS(item)) {
            return onClickFPS(this.pageContext, this.getArtifactLink(item.favorite), true, undefined, { telemetrySource: "FavoriteListProvider" });
        }
    };

    /**
     * Retrieves all favorites for the current user.
     * Notifies consumers as services resolve with favorites to allow for staggered rendering.
     * When extended data is retrieved, we wait for all services to resolve.
     */
    public getItems(): ObservableArray<IListBoxItem<FavoriteItem>> {
        if (!this.itemsPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

            const favoritesWithoutExtendedDetailsPromises = this.crossServiceFavoritesProvider.getFavoritesForScope(
                this.artifactScope.type,
                this.artifactScope.id,
                false /*extended details*/
            );
            const favoriteProviderPromise = this.favoritesService.getProviders();

            this.itemsPromise = favoriteProviderPromise.then((providers: { [artifactType: string]: FavoriteProvider }) => {
                const favoriteSettingsCollection = this.favoritesUserSettingsService.getFavoriteOrder();

                favoriteSettingsCollection.subscribe(this.onSettingsChanged);

                const clientServicesForProviders: Promise<IFavoriteProviderDataService | undefined>[] = [];
                Object.keys(providers).forEach((artifactType: string) => {
                    const provider = providers[artifactType];
                    if (provider.clientServiceIdentifier) {
                        const clientServicePromise = contributionService.getService<IFavoriteProviderDataService>(provider.clientServiceIdentifier);
                        if (clientServicePromise) {
                            clientServicesForProviders.push(clientServicePromise);
                        }
                    }
                });
                return Promise.all(clientServicesForProviders).then((services: (IFavoriteProviderDataService | undefined)[]) => {
                    this.providers = providers;
                    this.services = services;

                    favoritesWithoutExtendedDetailsPromises.then((servicePromises) => {
                        this.resolveFavoritesPromises(servicePromises, favoriteSettingsCollection);
                    });

                    this.crossServiceFavoritesProvider
                        .getFavoritesForScope(this.artifactScope.type, this.artifactScope.id, true /*extended details*/)
                        .then((servicePromises) => {
                            this.synchronizeFavoritesPromises(servicePromises, favoriteSettingsCollection);
                            this.isLoading = false;
                        });
                    return this.items;
                });
            });
        }

        return this.items;
    }

    /**
     * Waits for a set of favorites promises to all finish loading then notifies that its data set has changed.
     * @param favoritesProviderPromises Array of favorites promises.
     */
    private synchronizeFavoritesPromises(
        favoritesProviderPromises: Promise<Favorite[]>[],
        favoritesSettingsCollection: IFavoriteSettingsCollection
    ): void {
        if (favoritesProviderPromises.length) {
            Promise.all(favoritesProviderPromises).then((value: Favorite[][]) => {
                const array: Favorite[] = [];
                this.favorites = array.concat(...value);
                this.allFavoritesAreLoaded = true;
                this.onLoadCompleted && this.onLoadCompleted();
                this.setItemsAndGroups(favoritesSettingsCollection);
            });
        }
    }

    /**
     * Attaches a callback to a set of favorites provider promises so that each can notify consumers of updates to the internal list of favorites.
     * @param favoritesProviderPromises Array of favorites promises
     */
    private resolveFavoritesPromises(
        favoritesProviderPromises: Promise<Favorite[]>[],
        favoritesSettingsCollection: IFavoriteSettingsCollection
    ): void {
        if (favoritesProviderPromises.length) {
            favoritesProviderPromises.forEach((promise) => {
                // Whenever a favorites promise comes back from a service, render the updated set of favorites;
                promise.then((favorites) => {
                    if (favorites && !this.isLoaded()) {
                        this.favorites.push(...favorites);
                        this.setItemsAndGroups(favoritesSettingsCollection);
                    }
                });
            });
            Promise.all(favoritesProviderPromises).then(() => {
                this.allFavoritesAreLoaded = true;
                this.onLoadCompleted && this.onLoadCompleted();
            });
        }
    }

    /**
     * Favorites Should not have knowledge of project context information.
     * This function is used to resolve a high priority bug #1289264.
     * @param item The FavoriteItem that we are attempting to navigate to.
     */
    private canArtifactNavigateFPS(item: FavoriteItem): boolean {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const currentProjectScope = navigationService.getCurrentRoute().routeValues.project;

        return (
            item != null &&
            currentProjectScope != null &&
            item.favorite &&
            item.favorite.artifactScope &&
            item.favorite.artifactScope.type === FavoriteStorageScopes.Project &&
            item.favorite.artifactScope.name === currentProjectScope
        );
    }

    private onSettingsChanged = (values: FavoritesUserSettings[] | undefined): void => {
        if (values) {
            const mapping: { [pluralName: string]: string } = {};
            values.forEach((val) => {
                mapping[val.pluralName] = val.order.toString();
            });

            this.resolvedOrderedGroups = this.resolvedOrderedGroups!.map((group) => ({
                ...group,
                id: (group.name && mapping[group.name]) || group.id
            }));

            this.resolvedOrderedGroups.sort((a, b) => {
                return +a.id - +b.id;
            });
        }

        const favoriteSettingsCollection = this.favoritesUserSettingsService.getFavoriteOrder();
        this.setItemsAndGroups(favoriteSettingsCollection);
    };

    private renderGroupHeader = (
        group: IListBoxGroup,
        columnIndex: number,
        tableColumn: ITableColumn<IListBoxItem>,
        tableItem: IListBoxItem
    ): JSX.Element => {
        let itemIndicatorsSet: JSX.Element | undefined = undefined;

        const groups = this.getGroups();
        const groupIndex = groups.findIndex((g) => g.name == group.name);
        const groupToggleIndicators: IItemIndicatorProps[] = [];
        if (groupIndex > 0) {
            const upIndicator: IItemIndicatorProps = {
                iconProps: { iconName: "Up" },
                title: FavoritePanelResources.FavoriteToggle_MoveUp,
                onClick: () => this.onUpToggleClick(group)
            };
            groupToggleIndicators.push(upIndicator);
        }
        if (groupIndex < groups.length - 1) {
            const downIndicator: IItemIndicatorProps = {
                iconProps: { iconName: "Down" },
                title: FavoritePanelResources.FavoriteToggle_MoveDown,
                onClick: () => this.onDownToggleClick(group)
            };
            groupToggleIndicators.push(downIndicator);
        }

        if (groupToggleIndicators.length > 0) {
            itemIndicatorsSet = <ItemIndicatorSet indicators={groupToggleIndicators} />;
        }

        return (
            <SimpleTableCell
                className={css(
                    "favorite-groupHeader",
                    tableColumn.className,
                    tableItem.className,
                    tableItem.type === ListBoxItemType.Header && "bolt-list-box-header"
                )}
                columnIndex={columnIndex}
                key={group.name}
                tableColumn={tableColumn}
            >
                <span className={css("favorite-groupHeader-text")}>{tableItem.text}</span>
                {itemIndicatorsSet}
            </SimpleTableCell>
        );
    };

    private onUpToggleClick = (group: IListBoxGroup): void => {
        let index = this.getGroups().findIndex((groupItem) => groupItem.name === group.name);
        const groups = this.getGroups();
        const prevGroup = groups[index - 1];

        const updates: FavoritesUserSettings[] = [];

        if (group && group.name) {
            updates.push({
                pluralName: group.name,
                order: +prevGroup.id
            });
        }

        if (prevGroup && prevGroup.name) {
            updates.push({
                pluralName: prevGroup.name,
                order: +group.id
            });
        }

        this.favoritesUserSettingsService.setFavoriteOrderForArtifact(updates);
    };

    private onDownToggleClick = (group: IListBoxGroup): void => {
        let index = this.getGroups().findIndex((groupItem) => groupItem.name === group.name);
        const groups = this.getGroups();
        const nextGroup = groups[index + 1];

        const updates: FavoritesUserSettings[] = [];

        if (group && group.name) {
            updates.push({
                pluralName: group.name,
                order: +nextGroup.id
            });
        }

        if (nextGroup && nextGroup.name) {
            updates.push({
                pluralName: nextGroup.name,
                order: +group.id
            });
        }

        this.favoritesUserSettingsService.setFavoriteOrderForArtifact(updates);
    };

    private setItemsAndGroups(
        favoritesSettingsCollection: IFavoriteSettingsCollection
    ): void {
        const artifactToServiceMapping = this.createArtifactToServiceMapping(this.services);
        const pluralNameToOrderMapping = this.createPluralNameToOrderMapping(favoritesSettingsCollection);

        if (this.favorites && this.providers) {
            const itemsLocal: FavoriteItem[] = [];
            const itemGroupMappingLocal: { [artifactType: string]: IListBoxGroup } = {};
            const itemsFoundByArtifactType: { [artifactType: string]: Set<string> } = {};

            this.favorites.forEach((item) => {
                if (!item.artifactIsDeleted) {
                    let itemsFound = itemsFoundByArtifactType[item.artifactType];
                    if (!itemsFound) {
                        itemsFound = new Set<string>();
                    }

                    if (!itemsFound.has(item.artifactId)) {
                        itemsFound.add(item.artifactId);

                        const provider = this.providers[item.artifactType];
                        const href = this.getArtifactLink(item);

                        // don't render items which don't have favorite providers or hrefs (which means lack of metadata providers),
                        // as we would not have relevant data to present them properly.
                        if (provider && href) {
                            const listItem = {
                                favorite: item,
                                favoriteProvider: provider,
                                clientDataService: artifactToServiceMapping[item.artifactType]
                            };

                            itemsLocal.push(listItem);

                            const pluralName = listItem.favoriteProvider.pluralName;
                            const order = this.getOrderForGroup(pluralNameToOrderMapping, pluralName, listItem.favoriteProvider.order);

                            // Update dictionary with newly assigned order.
                            pluralNameToOrderMapping[pluralName] = order;
                            const group = {
                                id: order.toString(),
                                loading: false,
                                name: pluralName
                            };
                            itemGroupMappingLocal[item.artifactType] = group;
                        }
                    }
                }
            });
            this.itemGroupMapping = itemGroupMappingLocal; // this needs for getting the groups

            this.items.splice(0, this.items.length, ...this.createHeadersAndFavorites(itemsLocal));

            delete this.resolvedOrderedGroups; // With a new set of items to work with the old groups set is invalid.
        }
    }

    // Create an array that has all the headers, and favorite items in the right order.
    private createHeadersAndFavorites(favorites: FavoriteItem[]): any[] {
        const headersWithItems: { [artifactType: string]: (IListBoxGroup | any)[] } = {};

        // Sort favorites items alphabetically
        favorites.sort((favoriteItem1, favoriteItem2) => localeIgnoreCaseComparer(favoriteItem1.favorite.artifactName, favoriteItem2.favorite.artifactName));

        favorites.forEach((favoriteItem) => {
            const itemArtifactType = favoriteItem.favorite.artifactType;
            const group = this.itemGroupMapping[favoriteItem.favorite.artifactType];

            if (headersWithItems[itemArtifactType] === undefined) {
                headersWithItems[itemArtifactType] = [
                    {
                        id: group.id + "-header",
                        text: group.name || group.id,
                        type: ListBoxItemType.Header,
                        groupId: group.id,
                        render: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IListBoxItem<{}>>, tableItem: IListBoxItem<{}>) =>
                            this.renderGroupHeader(group, columnIndex, tableColumn, tableItem)
                    }
                ];
            }

            if (headersWithItems[itemArtifactType].length > 1) {
                headersWithItems[itemArtifactType].push({
                    id: group.id + "-divider",
                    type: ListBoxItemType.Divider,
                    groupId: group.id,
                    className: "favorite-item-divider"
                });
            }

            headersWithItems[itemArtifactType].push(this.getListItem(favoriteItem));
        });

        const headersDividersItems = Object.values(headersWithItems);

        // Sort them based on groupIds. Groups with higher groupId show up at the bottom of the favorites panel
        headersDividersItems.sort((groupA, groupB) => groupA[0].groupId - groupB[0].groupId);
        return headersDividersItems.flat();
    }

    private createPluralNameToOrderMapping(favoritesSettingsCollection: IFavoriteSettingsCollection): { [pluralName: string]: number } {
        const pluralNameToOrderMapping: { [pluralName: string]: number } = {};
        if (favoritesSettingsCollection && favoritesSettingsCollection.value) {
            favoritesSettingsCollection.value.forEach((item) => {
                pluralNameToOrderMapping[item.pluralName] = item.order;
            });
        }

        return pluralNameToOrderMapping;
    }

    private createArtifactToServiceMapping(services: (IFavoriteProviderDataService | undefined)[]): {
        [artifactType: string]: IFavoriteProviderDataService;
    } {
        const artifactToServiceMapping: { [artifactType: string]: IFavoriteProviderDataService } = {};
        if (services) {
            services.forEach((service) => {
                if (service) {
                    artifactToServiceMapping[service.getArtifactType()] = service;
                }
            });
        }

        return artifactToServiceMapping;
    }

    /**
     *  Assign order from the favorite provider by default. However, this may collide with
     *  the order of an existing group item - if so, assign the highest order value
     *  i.e. bottom of the list.
     **/
    private getOrderForGroup(pluralNameToOrderMapping: { [pluralName: string]: number }, pluralName: string, contributedOrder: number): number {
        let order = pluralNameToOrderMapping[pluralName];
        let orderAlreadyExists = false;
        let maxOrder = 0;

        if (order == undefined) {
            order = contributedOrder;

            for (const key in pluralNameToOrderMapping) {
                // Update maximum order.
                if (pluralNameToOrderMapping[key] > maxOrder) {
                    maxOrder = pluralNameToOrderMapping[key];
                }

                if (pluralNameToOrderMapping[key] === order) {
                    orderAlreadyExists = true;
                }
            }

            if (orderAlreadyExists) {
                // Assign the highest order value to this item, i.e. punt this group to bottom of the favorites list.
                order = maxOrder + 10;
            }
        }

        return order;
    }

    private getDisplayName(favoriteItem: FavoriteItem): string {
        return favoriteItem.clientDataService && typeof favoriteItem.clientDataService.getDisplayName === "function"
            ? favoriteItem.clientDataService.getDisplayName(favoriteItem.favorite)
            : favoriteItem.favorite.artifactName;
    }

    private getArtifactLink(favorite: Favorite): string {
        return ((favorite._links || {}).page || {}).href;
    }

    private toggleFavorite(favoriteItem: FavoriteItem): void {
        const id = favoriteItem.favorite.id;

        if (!this.favoritesRemoved) {
            this.favoritesRemoved = {};
        }

        if (this.favoritesRemoved[id]) {
            delete this.favoritesRemoved[id];
            this.favoritesService.addFavorite(favoriteItem.favorite);
        } else {
            this.favoritesRemoved[id] = favoriteItem;
            this.favoritesService.removeFavorite(favoriteItem.favorite);
        }
    }

    private isRemoved(favoriteItem: FavoriteItem): boolean {
        if (!this.favoritesRemoved) {
            return false;
        }

        return !!this.favoritesRemoved[favoriteItem.favorite.id];
    }

    public subscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<FavoriteItem>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): (value: IObservableArrayEventArgs<IListBoxItem<FavoriteItem>>, action?: ObservableArrayAction) => void {
        return this.items.subscribe(observer, action);
    }

    public unsubscribe(
        observer: (value: IObservableArrayEventArgs<IListBoxItem<FavoriteItem>>, action?: ObservableArrayAction) => void,
        action?: ObservableArrayAction
    ): void {
        return this.items.unsubscribe(observer, action);
    }
}
